import * as _ from 'underscore';
import { AuthorizableType } from './AuthorizableType';
import {
  CapabilityInfo,
  ICapabilityInfo
} from './Capabilities';
import { PermissionRoleName } from './PermissionRole';

export interface CapabilitySection {
  name: PermissionRoleName;
  capabilities: ReadonlyArray<ICapabilityInfo>;
}

const CollectionSections = [
  { name: PermissionRoleName.View, capabilities: [CapabilityInfo.read] },
];

const DatabaseSections = [
  { name: PermissionRoleName.View, capabilities: [CapabilityInfo.read] },
  { name: PermissionRoleName.Publish, capabilities: [CapabilityInfo.write] },
  { name: PermissionRoleName.Administer, capabilities: [CapabilityInfo.changePermissions] },
];

const DataRoleSections = [
  { name: PermissionRoleName.View, capabilities: [CapabilityInfo.read] },
  { name: PermissionRoleName.Publish, capabilities: [CapabilityInfo.write] },
  { name: PermissionRoleName.Administer, capabilities: [CapabilityInfo.changeHierarchy, CapabilityInfo.delete, CapabilityInfo.changePermissions] },
];

const DatasourceSections = (personalSpaceEnabled?: boolean, editPublishedDatasource?: boolean) => [
  { name: PermissionRoleName.View, capabilities: [CapabilityInfo.read, CapabilityInfo.connect] },
  { name: PermissionRoleName.Explore, capabilities: [CapabilityInfo.exportXmlDatasource] },
  { name: PermissionRoleName.Publish, capabilities: [CapabilityInfo.write, ...editPublishedDatasource ? [CapabilityInfo.saveAsDatasource] : []] },
  { name: PermissionRoleName.Administer, capabilities: [...personalSpaceEnabled ? [CapabilityInfo.changeHierarchy] : [], CapabilityInfo.delete, CapabilityInfo.changePermissions] },
];

const FlowSections = (editingFlowsEnabled?: boolean) => [
  { name: PermissionRoleName.View, capabilities: [CapabilityInfo.read] },
  { name: PermissionRoleName.Explore, capabilities: [CapabilityInfo.exportXmlFlow] },
  { name: PermissionRoleName.Publish, capabilities: [CapabilityInfo.executeFlow, CapabilityInfo.write, ...editingFlowsEnabled ? [CapabilityInfo.webAuthoringForFlows] : []] },
  { name: PermissionRoleName.Administer, capabilities: [CapabilityInfo.changeHierarchy, CapabilityInfo.delete, CapabilityInfo.changePermissions] },
];

const LensSections = [
  { name: PermissionRoleName.View, capabilities: [CapabilityInfo.read] },
  { name: PermissionRoleName.Publish, capabilities: [CapabilityInfo.write] },
  { name: PermissionRoleName.Administer, capabilities: [CapabilityInfo.changeHierarchy, CapabilityInfo.delete, CapabilityInfo.changePermissions] },
];

const MetricsSections = [
  { name: PermissionRoleName.View, capabilities: [CapabilityInfo.read] },
  { name: PermissionRoleName.Publish, capabilities: [CapabilityInfo.write] },
  { name: PermissionRoleName.Administer, capabilities: [CapabilityInfo.changeHierarchy, CapabilityInfo.delete, CapabilityInfo.changePermissions] },
];

const PublishedConnectionsSections = [
  { name: PermissionRoleName.View, capabilities: [CapabilityInfo.read, CapabilityInfo.connect] },
  { name: PermissionRoleName.Publish, capabilities: [CapabilityInfo.editPublishedConnection] },
  { name: PermissionRoleName.Administer, capabilities: [CapabilityInfo.changeHierarchy, CapabilityInfo.delete, CapabilityInfo.changePermissions] },
];

const ProjectSections = [
  { name: PermissionRoleName.View, capabilities: [CapabilityInfo.read] },
  { name: PermissionRoleName.Publish, capabilities: [CapabilityInfo.writeProject] },
];

const TableSections = [
  { name: PermissionRoleName.View, capabilities: [CapabilityInfo.read] },
  { name: PermissionRoleName.Publish, capabilities: [CapabilityInfo.write] },
  { name: PermissionRoleName.Administer, capabilities: [CapabilityInfo.changePermissions] }
];

const ViewSections = [
  { name: PermissionRoleName.View, capabilities: [CapabilityInfo.read, CapabilityInfo.filter, CapabilityInfo.viewComments, CapabilityInfo.addComment, CapabilityInfo.exportImage, CapabilityInfo.exportData] },
  { name: PermissionRoleName.Explore, capabilities: [CapabilityInfo.shareView, CapabilityInfo.vud, CapabilityInfo.webAuthoring] },
  { name: PermissionRoleName.Administer, capabilities: [CapabilityInfo.delete, CapabilityInfo.changePermissions] },
];

const WorkbookSections = [
  { name: PermissionRoleName.View, capabilities: [CapabilityInfo.read, CapabilityInfo.filter, CapabilityInfo.viewComments, CapabilityInfo.addComment, CapabilityInfo.exportImage, CapabilityInfo.exportData] },
  { name: PermissionRoleName.Explore, capabilities: [CapabilityInfo.shareView, CapabilityInfo.vud, CapabilityInfo.webAuthoring] },
  { name: PermissionRoleName.Publish, capabilities: [CapabilityInfo.exportXmlWorkbook, CapabilityInfo.write] },
  { name: PermissionRoleName.Administer, capabilities: [CapabilityInfo.changeHierarchy, CapabilityInfo.delete, CapabilityInfo.changePermissions] },
];

// NOTE: This mapping gets initialized in PermissionsDialog. To add a new capability behind a FF,
// the FF should be passed in as a parameter so it can be used to build the desired capability sections.
export const CapabilitySectionsByAuthorizableType = (editingFlowsEnabled?: boolean, personalSpaceEnabled?: boolean, editPublishedDatasource?: boolean): _.Dictionary<ReadonlyArray<CapabilitySection>> => {
  return {
    [AuthorizableType.COLLECTION]: CollectionSections,
    [AuthorizableType.DATABASE]: DatabaseSections,
    [AuthorizableType.DATABASE_TABLE_TEMPLATE]: TableSections,
    [AuthorizableType.DATAROLE]: DataRoleSections,
    [AuthorizableType.LENS]: LensSections,
    [AuthorizableType.DATASOURCE]: DatasourceSections(personalSpaceEnabled, editPublishedDatasource),
    [AuthorizableType.FLOW]: FlowSections(editingFlowsEnabled),
    [AuthorizableType.METRIC]: MetricsSections,
    [AuthorizableType.PUBLISHED_CONNECTION]: PublishedConnectionsSections,
    [AuthorizableType.PROJECT]: ProjectSections,
    [AuthorizableType.PROJECT_DATASOURCE_TEMPLATE]: DatasourceSections(personalSpaceEnabled, editPublishedDatasource),
    [AuthorizableType.PROJECT_DATA_ROLE_TEMPLATE]: DataRoleSections,
    [AuthorizableType.PROJECT_LENS_TEMPLATE]: LensSections,
    [AuthorizableType.PROJECT_FLOW_TEMPLATE]: FlowSections(editingFlowsEnabled),
    [AuthorizableType.PROJECT_METRIC_TEMPLATE]: MetricsSections,
    [AuthorizableType.PROJECT_PUBLISHED_CONNECTION_TEMPLATE]: PublishedConnectionsSections,
    [AuthorizableType.PROJECT_WORKBOOK_TEMPLATE]: WorkbookSections,
    [AuthorizableType.TABLE]: TableSections,
    [AuthorizableType.VIEW]: ViewSections,
    [AuthorizableType.WORKBOOK]: WorkbookSections,
  };
};
